import React, { useState } from "react";
import OtpInput from "react-otp-input";
import toast from "react-hot-toast";
import AuthService from "../../services/authService";
import LearnService from "../../services/LearnService";
import logo from "../../assets/creedom_logo.png";
import { useNavigate, useParams } from "react-router-dom";

const SubscriptionLogin = ({ onClose }) => {
  const [phone, setPhone] = useState("");
  const { processId } = useParams(); 
  const [isValid, setIsValid] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [disableVerifyOTPBtn, setDisableVerifyOTPBtn] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [promotionalData, setPromotionalData] = useState(null);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch promotional data on mount
  React.useEffect(() => {
    async function fetchData() {
      try {
        const res = await LearnService.getPromotionalDetail();
        const data = res.data || {};
        setPromotionalData(data);
      } catch (e) {
        // handle error
      }
    }
    fetchData();
  }, []);

  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(value);
    setIsValid(value.length === 10 && /^[6-9]/.test(value));
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setIsVerifying(true);
    try {
      await AuthService.generateOTP(phone);
      setShowOtpInput(true);
      toast.success("OTP sent!");
    } catch {
      toast.error("Failed to send OTP");
    }
    setIsVerifying(false);
  };

  const handleOtpChange = (value) => {
    setOtp(value);
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 4) return;
    setIsOtpVerifying(true);
    try {
      const otpResponse = await AuthService.otpLogin(phone, otp);
      console.log("OTP API Response:", otpResponse);

      if (otpResponse && otpResponse?.access_token) {
        const processedUserData = await getProperUserDetail(otpResponse);
        localStorage.setItem("access_token", otpResponse.access_token);
        localStorage.setItem("user_detail", JSON.stringify(processedUserData));
        localStorage.setItem(
          "user_otpResponse_data",
          JSON.stringify(otpResponse)
        );
        localStorage.setItem("user_id", otpResponse.user?._id || "");
        setOtpError("");
        toast.success("Login successful!");

        const membership =
          otpResponse.user?.membership || otpResponse.user?.memberShip;
        if (membership && membership.toUpperCase() === "PRO") {
          navigate("/subscribe-screen");
        } else {
          // Set flag to auto-initiate payment
          localStorage.setItem("auto_initiate_payment", "1");
          navigate(`/learn/paywall/${processId}`);
        }
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      setOtpError("Invalid OTP. Please try again.");
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleRazorpayPayment = async () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      navigate(`/learn/login/${processId}`);
      return;
    }
    const userDetail = JSON.parse(localStorage.getItem("user_detail") || "{}");
    const profileDetails = JSON.parse(localStorage.getItem("profileDetail") || "{}");
    if (
      userDetail?.user?.membership === "PRO" ||
      profileDetails?.userId?.membership === "PRO"
    ) {
      navigate("/subscribe-screen");
      return;
    }
    try {
      setIsPaymentLoading(true);
      const subscriptionBody = {
        planId: promotionalData?.planId,
        provider: "razorpay",
        authAmount: promotionalData?.authInfo?.amount,
        validity: promotionalData?.authInfo?.validity,
        validityType: promotionalData?.authInfo?.validityType,
        sourceId: processId,
        sourceType: "process",
        itemId: promotionalData?.itemId,
        providerId: "1",
      };
      const subscriptionResponse = await AuthService.createCourseSubscription(
        subscriptionBody
      );
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        name: "Creedom",
        order_id: subscriptionResponse?.data?.subscriptionDetails?.id,
        customer_id:
          subscriptionResponse?.data?.subscriptionDetails?.customerId,
        recurring: "1",
        notes: {
          userId: userDetail?.user?._id,
          userReferenceId:
            subscriptionResponse?.data?.subscriptionDetails?.customerId,
          razorpayOrderId: subscriptionResponse?.data?.subscriptionDetails?.id,
        },
        theme: { color: "#3399cc" },
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
          emi: false,
          emandate: false,
          paylater: false,
        },
        prefill: {
          email:
            userDetail?.user?.emailId ||
            `${userDetail?.user?.phoneNumber}@gmail.com`,
          contact: userDetail?.user?.phoneNumber,
          name: userDetail?.user?.userName,
        },
        handler: function (response) {
          toast.success("Payment successful!");
          setIsPaymentLoading(false);
          updateUserProfileAfterPayment();
          navigate("/subscription-success");
        },
        modal: {
          ondismiss: function () {
            toast("Payment popup closed");
            setIsPaymentLoading(false);
          },
        },
      });
      localStorage.setItem("razorpay_open_from_login", "1");
      rzp.open();
      // After 1 second, close login and navigate to payment screen in background
      setTimeout(() => {
        if (typeof onClose === 'function') {
          onClose();
        }
        navigate(`/learn/paywall/${processId}`);
      }, 1000);
    } catch (error) {
      console.error("Payment initiation failed:", error);
      toast.error("Payment initiation failed");
      setIsPaymentLoading(false);
    }
  };

  // Update user profile after payment
  const updateUserProfileAfterPayment = async () => {
    try {
      const userProfileFilterData = JSON.parse(
        localStorage.getItem("user_profile_filter_data") || "{}"
      );
      const profileId = userProfileFilterData?._id;
      if (profileId) {
        const updatedUserData = await AuthService.getProfileDetail(profileId);
        localStorage.setItem("profileDetail", JSON.stringify(updatedUserData));
      }
    } catch (error) {
      // ignore
    }
  };

  // Function to filter and process user profile data
  const getProperUserDetail = async (userData) => {
    const filteredProfiles = userData?.profile?.filter(
      (profile) => profile?.type !== "Expert"
    );

    const profileDetail = filteredProfiles[0];
    const updatedMainObject = {
      ...userData,
      profileDetail,
    };
    console.log("updatedMainObject", profileDetail);

    // Store filtered profile data in localStorage
    localStorage.setItem(
      "user_profile_filter_data",
      JSON.stringify(profileDetail)
    );

    return updatedMainObject;
  };

  const handleOTPInputChange = (value) => {
    setOtp(value);
    if (value.length === 4) {
      setDisableVerifyOTPBtn(true);
    } else {
      setDisableVerifyOTPBtn(false);
    }
  };

  React.useEffect(() => {
    if (localStorage.getItem("auto_initiate_payment") === "1") {
      localStorage.removeItem("auto_initiate_payment");
      handleRazorpayPayment();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #000 60%, #181818 100%)",
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        // justifyContent: "center",
        alignItems: "center",
        color: "#fff",
        position: "fixed",
        top: 100,
        left: 0,
        zIndex: 10001,
      }}
    >
      {/* Close Button */}
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 24,
            right: 24,
            background: "none",
            border: "none",
            color: "#fff",
            fontSize: 28,
            cursor: "pointer",
          }}
          aria-label="Close"
        >
          ×
        </button>
      )}

      {/* Logo and Title */}
      <div style={{ marginBottom: 32, textAlign: "center" }}>
        <img src={logo} alt="Logo" style={{ height: 48, marginBottom: 16 }} />
      </div>

      <form
        onSubmit={showOtpInput ? handleOtpSubmit : handleVerify}
        style={{
          width: "90%",
          maxWidth: 400,
          background: "rgba(24,24,24,0.95)",
          borderRadius: 20,
          padding: "40px 24px",
          boxShadow: "0 4px 32px #0008",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ width: "100%", marginBottom: 24 }}>
          <label
            htmlFor="mobile"
            style={{
              alignSelf: "flex-start",
              fontWeight: 500,
              fontSize: 18,
              marginBottom: 12,
              fontFamily: 'Gilroy-medium',
              color: "#fff",
            }}
          >
            Mobile Number
          </label>
          <div
            style={{
              display: "flex",
              width: "100%",
              gap: 12,
              marginBottom: 0,
            }}
          >
            <span
              style={{
                background: "#222",
                color: "#fff",
                borderRadius: 12,
                padding: "0 12px",
                display: "flex",
                alignItems: "center",
                fontSize: 18,
                fontWeight: 500,
                border: "1.5px solid #444",
                height: 56,
              }}
            >
              +91
            </span>
            <input
              id="mobile"
              type="tel"
              placeholder="Enter phone number"
              value={phone}
              onChange={handleChange}
              style={{
                flex: 1,
                padding: "16px 12px",
                borderRadius: 12,
                border: "1.5px solid #444",
                background: "#111",
                color: "#fff",
                fontSize: 18,
                outline: "none",
                fontWeight: 500,
                height: 56,
              }}
              disabled={showOtpInput}
            />
          </div>
        </div>
        {!showOtpInput && (
          <button
            type="submit"
            disabled={!isValid || isVerifying}
            style={{
              background: isValid
                ? "linear-gradient(90deg, #EE35FF 0%, #FF8761 100%)"
                : "#444",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "16px 0",
              width: "100%",
              fontWeight: 700,
              fontSize: 18,
              cursor: isValid ? "pointer" : "not-allowed",
              marginTop: 24,
              marginBottom: 8,
              transition: "background 0.2s",
            }}
          >
            {isVerifying ? "Verifying..." : "Verify"}
          </button>
        )}

        {/* OTP Input */}
        {showOtpInput && (
          <div style={{ width: "100%" }}>
            <label
              htmlFor="otp"
              style={{
                fontWeight: 500,
                fontSize: 18,
                fontFamily: 'Gilroy-medium',
                marginBottom: 12,
                color: "#fff",
              }}
            >
              Enter OTP
            </label>
            <div
              className=""
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <OtpInput
                value={otp}
                shouldAutoFocus
                onChange={handleOTPInputChange}
                numInputs={4}
                isInputNum={true}
                type="number"
                inputStyle={{
                  width: "50px",
                  height: "56px",
                  marginRight: "20px",
                  borderWidth: 1.5,
                  backgroundColor: "rgb(17, 17, 17)",
                  borderRadius: 5,
                  fontSize: 20,
                  justifyContent: "center",
                  alignContent: "center",
                  color: "#FFF",
                  borderColor: "#444444",
                }}
                className="customOtpContainer"
                renderSeparator={<span></span>}
                renderInput={(props) => (
                  <input
                    {...props}
                    inputMode="numeric"
                    type="number"
                    pattern="[0-9]*"
                  />
                )}
              />
            </div>
            {otpError && (
              <div
                style={{
                  color: "#FF4D4F",
                  marginTop: 8,
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                {otpError}
              </div>
            )}
            <button
              type="submit"
              disabled={otp.length !== 4 || !!otpError || isOtpVerifying}
              style={{
                background:
                  otp.length === 4 && !otpError && !isOtpVerifying
                    ? "linear-gradient(90deg, #EE35FF 0%, #FF8761 100%)"
                    : "#444",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "16px 0",
                width: "100%",
                fontWeight: 700,
                fontSize: 18,
                cursor:
                  otp.length === 4 && !otpError && !isOtpVerifying
                    ? "pointer"
                    : "not-allowed",
                marginTop: 24,
                marginBottom: 8,
                transition: "background 0.2s",
              }}
            >
              {isOtpVerifying ? "Verifying..." : "Verify OTP"}
            </button>
          </div>
        )}
        <div
          style={{
            marginTop: 24,
            color: "#aaa",
            fontSize: 13,
            textAlign: "center",
          }}
        >
          <p
            className="ct-text-14 mb-2"
            style={{ fontWeight: 600, color: "#FFF" }}
          >
            By clicking on Verify, you agree to the{" "}
            <span
              className="ct-pointer"
              style={{
                textDecoration: "underline",
                color: "#EE35FF",
              }}
            >
              <a
                href="/termsConditions"
                target="_blank"
                style={{ color: "#ee35ff" }}
              >
                terms and conditions
              </a>
            </span>{" "}
            of Creedom 
          </p>
        </div>
      </form>
    </div>
  );
};

export default SubscriptionLogin;
